import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch,
    Stack, Badge, Tooltip, TextField,
} from '@mui/material';
import {Add, Home as HomeIcon, Warning} from '@mui/icons-material';
import {atualizaCategoria, deletaCategoria, listaCategorias} from './configs/functions';
import {DiversityIcon, EditIcon, TrashIcon} from '../../../common/Icons';
import { hasPermission, checkPermissionsAndRedirect } from '../../../../configs/functions';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import ConfirmDialog from '../../../common/ConfirmDialog';
import CategoryForm from './CategoryForm';
import { useAppContext } from '../../../../contexts/AppContext';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { listaEmpresas } from '../../management/companies/configs/functions';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import {CategoryTableFilter} from "./components/CategoryTableFilter";
import {FiltersButton} from "../../../common/FiltersButton";
import {useSearchParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const Row = ({ data, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);
    const [openStatusDialog, setOpenStatusDialog] = useState(false);
    const [openActiveStatusDialog, setOpenActiveStatusDialog] = useState(false);

    async function toggleStatus() {
        if (!hasPermission(["admin", "admin_categorias", "update_categorias"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaCategoria(data.categorias_id[0], { status: !active })
            if (res) {
                const message = active ? 'Categoria inativada com sucesso' : 'Categoria ativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.categorias_id[0], !active)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
        }
    }

    function handleToggleStatus() {
        if (active) {
            setOpenStatusDialog(true);
            return;
        }
        setOpenActiveStatusDialog(true);
        return;
    }

    async function confirmActive(){
        setOpenActiveStatusDialog(false);
        await toggleStatus();
    }

    async function confirmInactivate() {
        setOpenStatusDialog(false);
        await toggleStatus();
    }

    const deleteCategory = () => {
        const id = data.categorias_id[0];
        deletaCategoria(id)
            .then((response) => {
                const message = response.data.message ?? 'Categoria inativada com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
                updateData();
            }).catch((error) => {
            const message = error.response.data.message ?? 'Erro ao executar operação'
            handleExibirAlerta('Erro', message, 'error');
        })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                }}
            >
                {/*<TableCell sx={{ flex: 1, border: 0 }}>{data.categorias_id[0]}</TableCell>*/}
                <TableCell sx={{ flex: 2, border: 0 }}>{data.nome}</TableCell>
                <TableCell sx={{ flex: 3, border: 0 }}>{data.descricao}</TableCell>

                <TableCell sx={{ flex: 1, border: 0 }}>
                    <Switch
                        checked={!!active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_categorias", "update_categorias"])}
                    />
                </TableCell>

                <TableCell align='right' sx={{ flex: 1, border: 0, minWidth: 110 }}>
                    {hasPermission(["admin", "admin_categorias", "update_categorias"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            <EditIcon />
                        </IconButton>}

                    {hasPermission(["admin", "admin_categorias", "delete_categorias"]) &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>}
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir esta categoria?'}
                title={'Excluir Categoria'}
                goAction={deleteCategory}
                handleClose={setOpenDialog}
                state={openDialog}
            />

            <ConfirmDialog
                description={'Importante: a desativação da categoria inativa automaticamente as permissões e estoques. Uma futura reativação exigirá a liberação manual de cada permissão'}
                title={'Tem certeza que deseja inativar a categoria?'}
                goAction={confirmInactivate}
                handleClose={setOpenStatusDialog}
                state={openStatusDialog}
                icon ={<Warning fontSize='large'/>} 
            />

                <ConfirmDialog
                description={'Importante: A ativação da categoria exige a configuração manual subsequente de cada permissão específica.'}
                title={'Tem certeza que deseja ativar a categoria?'}
                goAction={confirmActive}
                handleClose={setOpenActiveStatusDialog}
                state={openActiveStatusDialog}
                icon ={<Warning fontSize='large'/>} 
            />
        </>
    )
}

function ListOfCategories() {
    const queryClient = useQueryClient();

    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const [openForm, setOpenForm] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;
            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;
            setEmpresaIdSession(selectedEmpresaId);
            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });


    const queryFilters = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        nome: searchParams.get('nome'),
        id_empresa: searchParams.get('id_empresa'),
    };

    const queryKeyListCategories = ['listOfCategories', queryFilters];
    const { data: categoriesData, isLoading, isPending, error: listOfCategoriesError } = useQuery({
        queryKey: queryKeyListCategories,
        queryFn: () => listaCategorias(
            queryFilters.id_empresa,
            queryFilters.offset,
            queryFilters.limit,
            queryFilters.nome,
        ),
        enabled: !!queryFilters.id_empresa,
    });
    const atualizarCategorias = () => {
    return queryClient.invalidateQueries({
        queryKey: ['listOfCategories'],
    });
};

    const categorias = categoriesData?.data?.data ?? [];
    const numTotalItems = categoriesData?.data?.numero_total ?? 0;
    const isLoadingList = isLoading || isPending;

    const { mutateAsync: handleUpdateStatusFn } = useMutation({
        mutationFn: (data) => atualizaCategoria(data.id, data.dataToUpdate),
        onSuccess: (response, variables, context) => {
            const newStatus = variables.dataToUpdate.status;

            const message = newStatus ? 'Categoria ativada com sucesso.' : 'Categoria inativada com sucesso.';
            exibirAlerta('Sucesso', message, 'success');

            queryClient.invalidateQueries({ queryKey: queryKeyListCategories });
        },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        }
    });

    function handleEditCategory(item) {
        setSelectedProduct(item);
        setOpenForm(true);
    }

    useEffect(() => {
        setSearchParams((state) => {
            state.set('page', String(page + 1));
            state.set('perPage', String(rowsPerPage));
            return state;
        });
    }, [page, rowsPerPage, setSearchParams]);

    useEffect(() => {
        if (openForm) return;
        setSelectedProduct(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_categorias", "list_categorias"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);


    return (<>
        <Helmet title="Categorias" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to='/'><HomeIcon fontSize='small' /></LinkNavegacao >
                    <LinkNavegacao to='/epis'>Gestão de EPI's</LinkNavegacao >
                    <LinkNavegacao to='/epis/produtos'>Produtos</LinkNavegacao >
                    <Typography variant='span'>Categorias</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<DiversityIcon fontSize='large' />}
                    title='Categorias'
                    description='Administre as categoriais disponíveis para seus produtos.'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_categorias", "create_categorias"]) &&
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                            >
                                Cadastrar nova categoria
                            </Button>
                        </Typography>
                    }
                </Stack>
            </Grid>

            <CategoryTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <Table aria-label="Categorias">
                        <TableHead>
                            <TableRow sx={{ display: 'flex' }}>
                                {/*<TableCell sx={{ flex: 1 }}>ID</TableCell>*/}
                                <TableCell sx={{ flex: 2 }}>Categoria</TableCell>
                                <TableCell sx={{ flex: 3 }}>Descrição</TableCell>
                                <TableCell sx={{ flex: 1 }}>Status</TableCell>
                                <TableCell sx={{ flex: 1, minWidth: 110, textAlign: 'right' }}>
                                    <Tooltip title="Os dados preenchidos servem apenas como uma base inicial de referência e não devem ser considerados definitivos.
                                                    É fundamental que sejam cuidadosamente revisados e personalizados de acordo com as características específicas de cada Grupo Homogêneo de Exposição (GHE),
                                                    garantindo assim a adequação e a precisão das informações para cada realidade analisada">
                                        <Warning sx={{ cursor: 'pointer' }} />
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? <LoadingTableRow />
                                : categorias && categorias?.length > 0
                                    ? categorias.map((item) =>
                                        <Row
                                            key={item.categorias_id[0]}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditCategory}
                                            updateData={categorias}
                                            handleUpdateStatus={(id, status) =>
                                                handleUpdateStatusFn({ id, dataToUpdate: { status } })
                                            }
                                        />
                                    )
                                    : <EmptyTableRow />
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        setRowsPerPage={setRowsPerPage}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                    />
                </TableContainer>
            </Grid>

        </Grid>

        <CategoryForm open={openForm} setOpen={setOpenForm} category={selectedProduct} id_empresas={empresasData?.data?.data}onSuccess={atualizarCategorias}/>
    </>);
}

export default ListOfCategories;
